import { Shelf } from "@/components/mobile/Shelf";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmailCapture } from "@/components/EmailCapture";
import { Library as LibraryIcon, BookOpen, ArrowRight, Search, X, Heart, Clock, Download } from "lucide-react";
import { RESOURCES, type Resource } from "@/lib/library-resources";
import { downloadFile } from "@/lib/download-file";
import { useIsMobile } from "@/hooks/use-mobile";
import { LIBRARY_CATEGORIES } from "@/lib/library-catalog";
import {
  listGeneratedArticles,
  searchArticleSlugs,
  expandArticleTerms,
  type ArticleMeta,
} from "@/lib/library-articles";
import { FavoriteButton } from "@/components/FavoriteButton";
import { StarRating } from "@/components/StarRating";
import { useFavoritesList } from "@/hooks/useFavorite";
import { LibraryDocumentsSection } from "@/components/LibraryDocumentsSection";
import { Highlight } from "@/components/Highlight";

const librarySearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  cat: fallback(z.string(), "").default(""),
  fav: fallback(z.boolean(), false).default(false),
  sort: fallback(z.string(), "recommended").default("recommended"),
});
type LibrarySearch = z.infer<typeof librarySearchSchema>;
type SortKey = "recommended" | "newest" | "oldest";
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "recommended", label: "Recommended" },
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
];

export const Route = createFileRoute("/library/")({
  validateSearch: zodValidator(librarySearchSchema),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Trauma Recovery Library — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Trauma-informed guides, workbooks, and recovery resources for CPTSD, PTSD, anxiety, inner child work, and nervous system regulation.",
      },
      { property: "og:title", content: "Trauma Healing Resources & Recovery Library" },
      {
        property: "og:description",
        content:
          "Trauma-informed guides, workbooks, and recovery resources for CPTSD, PTSD, anxiety, and nervous system healing.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/library" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/library" }],
  }),
  component: withMemberGate(LibraryPage, "Recovery Library"),
});

function matches(r: Resource, q: string) {
  if (!q) return true;
  const hay = `${r.title} ${r.blurb} ${r.kind} ${r.topics.join(" ")} ${r.body.intro}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

const QUICK_TAGS: { label: string; emoji: string; query: string }[] = [
  { label: "Grounding", emoji: "🏠", query: "grounding" },
  { label: "Nervous System", emoji: "🌿", query: "nervous system" },
  { label: "CPTSD", emoji: "🧠", query: "cptsd" },
];

// Soft, trauma-informed gradient palette per category — used for the small
// decorative thumbnail at the top of each article card.
/** Gentle rotating tints for the featured guide shelf. */
const FEATURED_TINTS = [
  "border-gold/30 bg-gradient-to-b from-gold/10 via-card to-card",
  "border-emerald-500/25 bg-gradient-to-b from-emerald-500/10 via-card to-card",
  "border-sky-500/25 bg-gradient-to-b from-sky-500/10 via-card to-card",
  "border-rose-500/25 bg-gradient-to-b from-rose-500/10 via-card to-card",
  "border-violet-500/25 bg-gradient-to-b from-violet-500/10 via-card to-card",
] as const;

const CATEGORY_GRADIENTS: Record<string, string> = {
  "trauma-recovery": "from-rose-100 via-amber-50 to-cream",
  "nervous-system": "from-emerald-100 via-teal-50 to-cream",
  "trauma-bonds": "from-pink-100 via-rose-50 to-cream",
  "inner-child": "from-amber-100 via-yellow-50 to-cream",
  "self-worth": "from-yellow-100 via-amber-50 to-cream",
  "boundaries": "from-sky-100 via-blue-50 to-cream",
  "relationships": "from-rose-100 via-pink-50 to-cream",
  "anxiety-depression": "from-violet-100 via-indigo-50 to-cream",
  "sleep-dreams": "from-indigo-100 via-violet-50 to-cream",
  "somatic": "from-teal-100 via-emerald-50 to-cream",
  "shadow-work": "from-purple-100 via-violet-50 to-cream",
  "creativity": "from-fuchsia-100 via-rose-50 to-cream",
  "grief": "from-slate-100 via-blue-50 to-cream",
  "spirituality": "from-amber-100 via-orange-50 to-cream",
};

const gradientFor = (categoryId?: string) =>
  (categoryId && CATEGORY_GRADIENTS[categoryId]) || "from-gold-light/60 via-cream to-cream";

/** Faint version of the category colour for the card body, so a list of
 *  articles reads as warm and varied rather than a row of white boxes. */
const CATEGORY_SURFACES: Record<string, string> = {
  "trauma-recovery": "border-rose-500/25 bg-gradient-to-b from-rose-500/8 via-card to-card",
  "nervous-system": "border-emerald-500/25 bg-gradient-to-b from-emerald-500/8 via-card to-card",
  "trauma-bonds": "border-pink-500/25 bg-gradient-to-b from-pink-500/8 via-card to-card",
  "inner-child": "border-amber-500/25 bg-gradient-to-b from-amber-500/8 via-card to-card",
  "self-worth": "border-yellow-500/25 bg-gradient-to-b from-yellow-500/8 via-card to-card",
  boundaries: "border-sky-500/25 bg-gradient-to-b from-sky-500/8 via-card to-card",
  relationships: "border-rose-500/25 bg-gradient-to-b from-rose-500/8 via-card to-card",
  "anxiety-depression": "border-violet-500/25 bg-gradient-to-b from-violet-500/8 via-card to-card",
  "sleep-dreams": "border-indigo-500/25 bg-gradient-to-b from-indigo-500/8 via-card to-card",
  somatic: "border-teal-500/25 bg-gradient-to-b from-teal-500/8 via-card to-card",
  "shadow-work": "border-purple-500/25 bg-gradient-to-b from-purple-500/8 via-card to-card",
  creativity: "border-fuchsia-500/25 bg-gradient-to-b from-fuchsia-500/8 via-card to-card",
  grief: "border-slate-500/25 bg-gradient-to-b from-slate-500/8 via-card to-card",
  spirituality: "border-amber-500/25 bg-gradient-to-b from-amber-500/8 via-card to-card",
};

const surfaceFor = (categoryId?: string) =>
  (categoryId && CATEGORY_SURFACES[categoryId]) ||
  "border-gold/25 bg-gradient-to-b from-gold/8 via-card to-card";

// Topic-specific emoji picked from keywords in the article title. Falls back
// to the category emoji when no specific match is found.
const TOPIC_EMOJI_RULES: { keywords: string[]; emoji: string }[] = [
  { keywords: ["cptsd", "complex trauma"], emoji: "🧠" },
  { keywords: ["ptsd"], emoji: "🛡️" },
  { keywords: ["flashback"], emoji: "⚡" },
  { keywords: ["hypervigilance", "trigger"], emoji: "👁️" },
  { keywords: ["window of tolerance"], emoji: "🪟" },
  { keywords: ["safety", "stabilis"], emoji: "🛟" },
  { keywords: ["polyvagal", "vagus"], emoji: "🌿" },
  { keywords: ["regulation", "regulate"], emoji: "⚖️" },
  { keywords: ["fight", "flight", "freeze", "fawn"], emoji: "🦌" },
  { keywords: ["co-regulation", "co regulation"], emoji: "🤝" },
  { keywords: ["somatic", "body"], emoji: "🧘" },
  { keywords: ["grounding"], emoji: "🌳" },
  { keywords: ["stress"], emoji: "🌊" },
  { keywords: ["resilience"], emoji: "🌱" },
  { keywords: ["narciss", "trauma bond"], emoji: "💔" },
  { keywords: ["inner child", "child"], emoji: "🧸" },
  { keywords: ["self-worth", "self worth", "esteem", "confidence"], emoji: "💛" },
  { keywords: ["boundar"], emoji: "🚧" },
  { keywords: ["relationship", "attach", "partner"], emoji: "💞" },
  { keywords: ["anxiety", "panic", "worry"], emoji: "🌬️" },
  { keywords: ["depress", "low mood"], emoji: "🌧️" },
  { keywords: ["sleep", "insomn"], emoji: "🌙" },
  { keywords: ["dream", "nightmare"], emoji: "💭" },
  { keywords: ["shadow"], emoji: "🌑" },
  { keywords: ["creativ", "art"], emoji: "🎨" },
  { keywords: ["grief", "loss", "mourn"], emoji: "🕯️" },
  { keywords: ["spirit", "meditat", "mindful"], emoji: "🕊️" },
  { keywords: ["breath"], emoji: "🌬️" },
  { keywords: ["heal", "recover"], emoji: "💗" },
  { keywords: ["journal", "reflect"], emoji: "📓" },
  { keywords: ["forgive"], emoji: "🤲" },
  { keywords: ["anger", "rage"], emoji: "🔥" },
  { keywords: ["shame", "guilt"], emoji: "🫥" },
];

function topicEmojiFor(title: string, fallback?: string): string {
  const t = title.toLowerCase();
  for (const rule of TOPIC_EMOJI_RULES) {
    if (rule.keywords.some((k) => t.includes(k))) return rule.emoji;
  }
  return fallback ?? "📄";
}

const SCROLL_KEY = "rt:library-scroll";

function LibraryPage() {
  const isMobile = useIsMobile();
  // URL is the source of truth for query, category, and favourites-only —
  // so refreshing or sharing the link restores the same filtered view.
  const { q: urlQuery, cat: urlCat, fav: urlFav, sort: urlSort } = Route.useSearch();
  const navigate = useNavigate({ from: "/library/" });

  // Coming back from a guide should land where you left off, not at the top.
  useEffect(() => {
    const saved = Number(sessionStorage.getItem(SCROLL_KEY) || "0");
    if (saved > 0) {
      requestAnimationFrame(() => window.scrollTo({ top: saved, behavior: "auto" }));
    }
    const remember = () => sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    window.addEventListener("scroll", remember, { passive: true });
    return () => {
      remember();
      window.removeEventListener("scroll", remember);
    };
  }, []);


  // Local input state mirrors the URL, but the URL is updated on a short
  // debounce so typing doesn't spam navigation.
  const [query, setQueryState] = useState(urlQuery);
  useEffect(() => { setQueryState(urlQuery); }, [urlQuery]);
  useEffect(() => {
    if (query === urlQuery) return;
    const id = window.setTimeout(() => {
      navigate({
        search: (prev: LibrarySearch) => ({ ...prev, q: query }),
        replace: true,
      });
    }, 250);
    return () => window.clearTimeout(id);
  }, [query, urlQuery, navigate]);

  const setQuery = useCallback((next: string) => setQueryState(next), []);
  const activeCategories = useMemo(
    () =>
      String(urlCat ?? "")
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean),
    [urlCat],
  );
  const toggleCategory = useCallback(
    (id: string | null) => {
      navigate({
        search: (prev: LibrarySearch) => {
          if (id === null) return { ...prev, cat: "" };
          const current = prev.cat
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const next = current.includes(id)
            ? current.filter((c) => c !== id)
            : [...current, id];
          return { ...prev, cat: next.join(",") };
        },
        replace: true,
      });
    },
    [navigate],
  );
  const favOnly = urlFav;
  const setFavOnly = useCallback(
    (updater: boolean | ((v: boolean) => boolean)) => {
      navigate({
        search: (prev: LibrarySearch) => ({
          ...prev,
          fav: typeof updater === "function" ? updater(prev.fav) : updater,
        }),
        replace: true,
      });
    },
    [navigate],
  );
  const sort: SortKey =
    urlSort === "newest" || urlSort === "oldest" ? urlSort : "recommended";
  const setSort = useCallback(
    (next: SortKey) => {
      navigate({
        search: (prev: LibrarySearch) => ({ ...prev, sort: next }),
        replace: true,
      });
    },
    [navigate],
  );

  const favList = useFavoritesList("rt:fav-library");
  const favIds = useMemo(() => new Set(favList.map((f) => f.id)), [favList]);
  const highlightTerms = useMemo(() => expandArticleTerms(query), [query]);

  // Suggestion chips inside empty states dispatch this event to prefill search.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail ?? "";
      setQueryState(detail);
      navigate({ search: (prev: LibrarySearch) => ({ ...prev, q: detail }), replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("library-search:set", handler as EventListener);
    return () => window.removeEventListener("library-search:set", handler as EventListener);
  }, [navigate]);

  const { featured, results } = useMemo(() => {
    let filtered = RESOURCES.filter((r) => matches(r, query));
    if (favOnly) filtered = filtered.filter((r) => favIds.has(r.slug));
    return {
      featured: filtered.filter((r) => r.kind === "PDF" && r.cover),
      results: filtered,
    };
  }, [query, favOnly, favIds]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
      <header className="mb-6 text-center md:mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <LibraryIcon className="h-3.5 w-3.5" /> Recovery Library
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground md:text-5xl">
          📚 Your Recovery Library
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Trauma-informed guides, tools, and recovery resources for every stage of your recovery
          journey.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Looking for public articles?{" "}
          <Link to="/blog" className="font-medium text-primary hover:underline">
            Read the Recovery Trauma™ Blog →
          </Link>
        </p>
      </header>

      {/* Primary search — hero treatment */}
      <section
        aria-label="Search the Recovery Library"
        className="sticky top-0 z-10 -mx-4 mb-8 bg-background/95 px-4 pb-5 pt-4 backdrop-blur md:static md:mx-0 md:mb-10 md:rounded-3xl md:border md:border-gold/25 md:bg-gradient-to-br md:from-gold-light/30 md:via-card md:to-cream/40 md:px-8 md:py-7 md:shadow-[var(--shadow-soft-lg)] md:backdrop-blur-none"
      >
        <div className="mx-auto max-w-3xl">
          <div className="mb-3 text-center">
            <h2 className="font-display text-lg text-foreground md:text-2xl">
              🔍 Search the Recovery Library
            </h2>
            <p className="mt-1 text-xs text-muted-foreground md:text-sm">
              Find topics, tools, exercises, guides, articles, worksheets and resources.
            </p>
          </div>

          <label className="relative block">
            <span className="sr-only">Search library</span>
            <Search
              className="pointer-events-none absolute start-5 top-1/2 h-6 w-6 -translate-y-1/2 text-[var(--gold-deep)]"
              strokeWidth={2.25}
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search CPTSD, boundaries, grounding, sleep, anxiety…"
              className="w-full rounded-2xl border-2 border-gold/40 bg-card py-4 ps-14 pe-12 text-base text-foreground shadow-[0_4px_18px_-6px_color-mix(in_oklab,var(--gold)_40%,transparent)] outline-none transition-all placeholder:text-muted-foreground/80 focus:border-[var(--gold-deep)] focus:shadow-[var(--shadow-gold)] md:py-5 md:ps-16 md:text-lg"
              aria-label="Search the Recovery Library"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute end-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </label>

          {/* Quick search tags */}
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between px-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Popular topics
              </p>
              <button
                type="button"
                onClick={() => setFavOnly((v) => !v)}
                aria-pressed={favOnly}
                className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  favOnly
                    ? "border-rose-300 bg-rose-50 text-rose-600"
                    : "border-border bg-card text-muted-foreground hover:border-rose-200 hover:text-rose-500"
                }`}
              >
                <Heart className={`h-3 w-3 ${favOnly ? "fill-current" : ""}`} />
                Favourites only
              </button>
            </div>
            <div className="relative">
              <div
                className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                onScroll={(e) => {
                  // Hide gradient + hint once user starts scrolling
                  e.currentTarget.parentElement?.setAttribute("data-scrolled", "1");
                }}
              >
                {QUICK_TAGS.map((t) => {
                  const active = query.toLowerCase() === t.query.toLowerCase();
                  return (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => setQuery(active ? "" : t.query)}
                      aria-pressed={active}
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        active
                          ? "border-transparent bg-[var(--gold-deep)] text-background shadow-sm"
                          : "border-gold/30 bg-card text-foreground hover:border-gold/60 hover:bg-gold-light/30"
                      }`}
                    >
                      <span aria-hidden>{t.emoji}</span>
                      {t.label}
                    </button>
                  );
                })}
              </div>
              {/* Right-edge fade affordance — disappears after first scroll */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 end-0 w-12 bg-gradient-to-l from-background to-transparent transition-opacity sm:hidden [[data-scrolled='1']>&]:opacity-0"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-1 end-1 flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground/70 ring-1 ring-border/60 backdrop-blur-sm sm:hidden [[data-scrolled='1']>&]:opacity-0"
              >
                Swipe <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </div>

          {query && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              {results.length} {results.length === 1 ? "result" : "results"} for &ldquo;{query}&rdquo;
            </p>
          )}
        </div>
      </section>

      {/* Featured guides — main PDF guides at the top */}
      {featured.length > 0 && (
        <section className="mb-10">
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="font-display text-xl text-foreground md:text-2xl">Featured guides</h2>
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              Read &amp; download
            </span>
          </div>

          <Shelf cols={4} cardWidth="sm">
            {featured.map((r, i) => (
              <div
                key={r.slug}
                className={`group relative flex flex-col overflow-hidden rounded-xl border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] ${FEATURED_TINTS[i % FEATURED_TINTS.length]}`}
              >

                <Link
                  to="/library/$slug"
                  params={{ slug: r.slug }}
                  className="block aspect-[2/3] overflow-hidden bg-cream/40"
                  aria-label={`Open ${r.title}`}
                >
                  <img
                    src={r.cover}
                    alt={`${r.title} cover`}
                    loading="lazy"
                    className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </Link>
                <FavoriteButton
                  storageKey="rt:fav-library"
                  id={r.slug}
                  label={r.title}
                  sub={r.blurb}
                  to={`/library/${r.slug}`}
                  size="sm"
                  className="absolute end-2 top-2 h-7 w-7 shadow-sm backdrop-blur"
                />
                <div className="flex flex-1 flex-col gap-1.5 p-2.5">
                  <span className="inline-flex w-fit items-center rounded-full bg-gold/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                    {r.kind}
                  </span>
                  <h3 className="line-clamp-2 font-display text-xs leading-snug text-foreground">
                    <Highlight text={r.title} terms={highlightTerms} />
                  </h3>
                  <div className="mt-auto flex flex-col gap-1.5 pt-1">
                    <StarRating id={r.slug} storageKey="rt:rating-library" size="sm" />
                    <div className="flex items-center justify-between gap-1.5">
                      {r.href ? (
                        <a
                          href={r.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-full bg-[var(--gold-deep)] px-2 py-1 text-[11px] font-semibold text-background"
                          aria-label={`Read ${r.title}`}
                        >
                          <BookOpen className="h-3 w-3" /> Read
                        </a>
                      ) : (
                        <Link
                          to="/library/$slug"
                          params={{ slug: r.slug }}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-full bg-[var(--gold-deep)] px-2 py-1 text-[11px] font-semibold text-background"
                          aria-label={`Read ${r.title}`}
                        >
                          <BookOpen className="h-3 w-3" /> Read
                        </Link>
                      )}
                      {r.href &&
                        (isMobile ? (
                          <span className="flex-1 text-center text-[10px] leading-tight text-muted-foreground">
                            Downloads are available on the desktop version.
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void downloadFile(r.href!, `${r.slug}.pdf`);
                            }}
                            className="inline-flex flex-1 items-center justify-center gap-1 rounded-full border border-gold/40 bg-card px-2 py-1 text-[11px] font-semibold text-[var(--gold-deep)]"
                            aria-label={`Download ${r.title}`}
                          >
                            <Download className="h-3 w-3" /> Download
                          </button>
                        ))}
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </Shelf>
        </section>
      )}

      {/* Uploaded documents from the Recovery Trauma team */}
      <LibraryDocumentsSection query={query} />

      {/* Educational articles have moved to the public Blog at /blog. */}



      <EmailCapture source="library" />
    </div>
  );
}

function CatalogSection({
  query,
  activeCategories,
  toggleCategory,
  sort,
  setSort,
}: {
  query: string;
  activeCategories: string[];
  toggleCategory: (id: string | null) => void;
  sort: SortKey;
  setSort: (next: SortKey) => void;
}) {
  const activeCategorySet = useMemo(() => new Set(activeCategories), [activeCategories]);
  const hasActiveCategory = activeCategories.length > 0;
  const favList = useFavoritesList("rt:fav-library");
  const favIds = useMemo(() => new Set(favList.map((f) => f.id)), [favList]);

  const all: ArticleMeta[] = useMemo(() => listGeneratedArticles(), []);

  // Build categories list from catalog (preserves order/emoji/blurb), plus
  // counts of generated articles in each.
  const categories = useMemo(() => {
    return LIBRARY_CATEGORIES.map((c) => {
      const slugs = new Set(c.articles.map((a) => a.slug));
      const count = all.filter((a) => slugs.has(a.slug)).length;
      return { ...c, count };
    });
  }, [all]);

  // Map article slug -> {emoji, categoryLabel, categoryId} via catalog.
  const slugToMeta = useMemo(() => {
    const m = new Map<string, { emoji: string; categoryId: string; categoryLabel: string }>();
    for (const c of LIBRARY_CATEGORIES) {
      for (const a of c.articles) {
        m.set(a.slug, { emoji: c.emoji, categoryId: c.id, categoryLabel: c.label });
      }
    }
    return m;
  }, []);

  const matchingSlugs = useMemo(() => searchArticleSlugs(query), [query]);
  const highlightTerms = useMemo(() => expandArticleTerms(query), [query]);

  // Catalog order treated as chronological — later entries are considered newer.
  const catalogIndex = useMemo(() => {
    const m = new Map<string, number>();
    all.forEach((a, i) => m.set(a.slug, i));
    return m;
  }, [all]);

  const filtered = useMemo(() => {
    const base = all.filter((a) => {
      if (!matchingSlugs.has(a.slug)) return false;
      if (hasActiveCategory) {
        const meta = slugToMeta.get(a.slug);
        if (!meta || !activeCategorySet.has(meta.categoryId)) return false;
      }
      return true;
    });
    if (sort === "recommended") return base;
    const sorted = [...base].sort(
      (a, b) => (catalogIndex.get(a.slug) ?? 0) - (catalogIndex.get(b.slug) ?? 0),
    );
    return sort === "newest" ? sorted.reverse() : sorted;
  }, [all, matchingSlugs, hasActiveCategory, activeCategorySet, slugToMeta, sort, catalogIndex]);

  const hasQuery = query.trim().length > 0;
  const isFiltering = hasQuery || hasActiveCategory;
  const visibleArticles = isFiltering ? filtered : filtered.slice(0, 6);
  const SUGGESTIONS = ["coping", "DBT skills", "nervous system", "self-worth", "boundaries", "grief"];

  return (
    <section className="mb-14 mt-2" aria-label="Recovery Library articles">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-xl text-foreground md:text-2xl">
          Recovery Library articles
        </h2>
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          {isFiltering
            ? `${filtered.length} of ${all.length}`
            : `${all.length} articles`}
        </span>
      </div>
      <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
        Trauma-informed educational articles across {categories.length} categories.
        Filter by category or use the search above to find a topic.
      </p>

      {/* Category filter chips */}
      <div className="mb-5 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          onClick={() => toggleCategory(null)}
          aria-pressed={!hasActiveCategory}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            !hasActiveCategory
              ? "border-transparent bg-[var(--gold-deep)] text-background shadow-sm"
              : "border-gold/30 bg-card text-foreground hover:border-gold/60 hover:bg-gold-light/30"
          }`}
        >
          All <span className="opacity-70">({all.length})</span>
        </button>
        {categories
          .map((c) => {
            // When searching, show per-category count restricted to matches.
            const matchCount = hasQuery
              ? c.articles.filter((a) => matchingSlugs.has(a.slug)).length
              : c.count;
            return { ...c, matchCount };
          })
          .filter((c) => c.matchCount > 0)
          .map((c) => {
            const active = activeCategorySet.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                aria-pressed={active}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? "border-transparent bg-[var(--gold-deep)] text-background shadow-sm"
                    : "border-gold/50 bg-card text-foreground hover:border-[var(--gold-deep)] hover:bg-gold-light/40"
                }`}
              >
                <span aria-hidden>{c.emoji}</span>
                {c.label}
                <span className="opacity-70">({c.matchCount})</span>
              </button>
            );
          })}
      </div>

      {/* Sort controls */}
      <div
        className="mb-5 -mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible"
        role="group"
        aria-label="Sort articles"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Sort
        </span>
        {SORT_OPTIONS.map((opt) => {
          const active = sort === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSort(opt.key)}
              aria-pressed={active}
              className={`inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? "border-transparent bg-[var(--gold-deep)] text-background shadow-sm"
                  : "border-gold/30 bg-card text-foreground hover:border-gold/60 hover:bg-gold-light/30"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>



      {filtered.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-10 text-center"
          role="status"
          aria-live="polite"
        >
          <p className="font-display text-base text-foreground">
            {hasQuery
              ? <>No articles match &ldquo;{query}&rdquo;{hasActiveCategory ? " in the selected categories" : ""}.</>
              : "No articles match this filter."}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try a different keyword — search understands common shorthand
            (e.g. <span className="italic">coping</span>, <span className="italic">DBT</span>,{" "}
            <span className="italic">somatic</span>).
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  toggleCategory(null);
                  window.dispatchEvent(new CustomEvent("library-search:set", { detail: s }));
                }}
                className="inline-flex items-center rounded-full border border-border/60 bg-background px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-gold/50 hover:text-foreground"
              >
                Try &ldquo;{s}&rdquo;
              </button>
            ))}
            {(hasActiveCategory || hasQuery) && (
              <button
                type="button"
                onClick={() => {
                  toggleCategory(null);
                  window.dispatchEvent(new CustomEvent("library-search:set", { detail: "" }));
                }}
                className="inline-flex items-center rounded-full bg-[var(--gold-deep)] px-3 py-1 text-xs font-semibold text-background hover:opacity-90"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <Shelf cols={3} cardWidth="md">
          {visibleArticles.map((a) => {
            const meta = slugToMeta.get(a.slug);
            const isFav = favIds.has(a.slug);
            return (
              <li
                key={a.slug}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-soft-lg)] ${surfaceFor(meta?.categoryId)}`}
              >

                {/* Soft decorative thumbnail — category-tinted */}
                <div
                  aria-hidden
                  className={`relative h-20 w-full overflow-hidden bg-gradient-to-br ${gradientFor(meta?.categoryId)}`}
                >
                  {/* Floating soft circles for a gentle, hand-drawn feel */}
                  <span className="absolute -start-3 -top-3 h-12 w-12 rounded-full bg-white/40 blur-md" />
                  <span className="absolute end-4 top-3 h-6 w-6 rounded-full bg-white/50 blur-[2px]" />
                  <span className="absolute bottom-2 start-8 h-3 w-3 rounded-full bg-white/60" />
                  <span className="absolute end-8 bottom-3 text-xs opacity-70">✨</span>
                  <span className="absolute start-3 bottom-2 text-[10px] opacity-60">💛</span>
                  {/* Big category emoji as the focal illustration */}
                  <span
                    className="absolute inset-0 flex items-center justify-center text-4xl drop-shadow-sm transition-transform duration-500 group-hover:scale-110"
                  >
                    {topicEmojiFor(a.title, meta?.emoji)}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="inline-flex shrink-0 items-center rounded-full bg-gold/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                    Article
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-muted-foreground">
                    <Clock className="h-3 w-3" /> {a.readMin} min read
                  </span>
                </div>

                <Link
                  to="/library/$slug"
                  params={{ slug: a.slug }}
                  className="font-display text-sm font-medium leading-snug text-foreground hover:text-[var(--gold-deep)]"
                >
                  <Highlight text={a.title} terms={highlightTerms} />
                </Link>

                {meta && (
                  <p className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {meta.categoryLabel}
                  </p>
                )}

                {a.blurb && (
                  <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                    <Highlight text={a.blurb} terms={highlightTerms} />
                  </p>
                )}

                <div className="mt-3 flex items-center justify-between gap-2 pt-1">
                  <StarRating id={a.slug} storageKey="rt:rating-library" size="sm" />
                  <Link
                    to="/library/$slug"
                    params={{ slug: a.slug }}
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]"
                  >
                    Read <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
                </div>

                <FavoriteButton
                  storageKey="rt:fav-library"
                  id={a.slug}
                  label={a.title}
                  sub={meta?.categoryLabel}
                  to={`/library/${a.slug}`}
                  size="sm"
                  className={`absolute end-2 top-2 h-7 w-7 ${isFav ? "" : ""}`}
                />
              </li>
            );
          })}
        </Shelf>
      )}
    </section>
  );
}

